import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { parseDomainList } from '@cp/shared';
import { seoMetricsApi } from '@/api/seoMetrics';
import { queryKeys } from '@/lib/queryKeys';
import { useApiMutation, type MutationCallbacks } from '@/hooks/useApiMutation';
import { errorText } from '@/utils/errors';
import type { DomainMetricsView, MetricSource } from '@/types/api';

export function useSeoMetricsNetwork() {
  return useQuery({ queryKey: queryKeys.seoMetrics.network, queryFn: seoMetricsApi.network });
}

/**
 * One site's SEO data, asked again while a read is on its way: every ten
 * seconds when one was asked for (the worker takes a couple of domains a
 * minute), every minute before the first read.
 */
export function useSiteSeoMetrics(siteId: string) {
  return useQuery({
    queryKey: queryKeys.seoMetrics.site(siteId),
    queryFn: () => seoMetricsApi.site(siteId),
    refetchInterval: (query) => (query.state.data?.pending ? 10_000 : query.state.data && !query.state.data.wayback ? 60_000 : false),
  });
}

/** «Обновить»: every source read now; the answer is the fresh view, put straight on the card. */
export function useRefreshSeoMetrics(siteId: string, callbacks?: MutationCallbacks<DomainMetricsView & { sources: MetricSource[] }, void>) {
  const client = useQueryClient();
  return useApiMutation(() => seoMetricsApi.refresh(siteId), () => [queryKeys.seoMetrics.timeline(siteId)], {
    ...callbacks,
    onSuccess: (...args) => {
      client.setQueryData(queryKeys.seoMetrics.site(siteId), args[0]);
      return callbacks?.onSuccess?.(...args);
    },
  });
}

/** A site's figures over time (ADR-0054). */
export function useSeoTimeline(siteId: string) {
  return useQuery({ queryKey: queryKeys.seoMetrics.timeline(siteId), queryFn: () => seoMetricsApi.timeline(siteId) });
}

export interface DomainCheckRun {
  /** The names being checked, in the order typed. */
  readonly domains: readonly string[];
  readonly results: Readonly<Record<string, DomainMetricsView>>;
  readonly errors: Readonly<Record<string, string>>;
  readonly invalid: readonly string[];
  readonly running: boolean;
  readonly done: number;
}

const IDLE: DomainCheckRun = { domains: [], results: {}, errors: {}, invalid: [], running: false, done: 0 };

/**
 * Names checked before buying (ADR-0050), one request a name, three at a
 * time: each row fills in as its answer comes, and the page counts them.
 */
export function useDomainCheck(): DomainCheckRun & { readonly run: (text: string) => void } {
  const [state, setState] = useState<DomainCheckRun>(IDLE);
  const current = useRef(0);
  useEffect(
    () => () => {
      // A run still going when the page closes stops writing into it.
      current.current += 1;
    },
    [],
  );

  const run = useCallback((text: string) => {
    const { domains, invalid } = parseDomainList(text);
    current.current += 1;
    const id = current.current;
    setState({ domains, results: {}, errors: {}, invalid, running: domains.length > 0, done: 0 });
    const queue = [...domains];
    const worker = async () => {
      for (let domain = queue.shift(); domain; domain = queue.shift()) {
        const name = domain;
        try {
          const answer = await seoMetricsApi.check(name);
          const result = answer.results[0];
          if (current.current !== id) return;
          setState((previous) => ({ ...previous, done: previous.done + 1, ...(result ? { results: { ...previous.results, [name]: result } } : {}) }));
        } catch (error) {
          if (current.current !== id) return;
          setState((previous) => ({ ...previous, done: previous.done + 1, errors: { ...previous.errors, [name]: errorText(error) } }));
        }
      }
    };
    void Promise.all([worker(), worker(), worker()]).then(() => {
      if (current.current === id) setState((previous) => ({ ...previous, running: false }));
    });
  }, []);

  return { ...state, run };
}
